/**
 * Per-starter Spark prefill registry.
 *
 * The website's segment cards link to `/spark?starter=<slug>` (in
 * kebab-case, e.g. `?starter=achievement-proof`). The SparkPage
 * looks up the slug here to pre-populate Step 1's name, description,
 * and domain. Data source: `ui/src/data/segments.ts` (segment +
 * starter ids, engine domain) joined with
 * `ui/src/i18n/locales/en/segments.json` (starter name + oneliner).
 *
 * Implementation note: the `starters` block in segments.json keys
 * starters by their snake_case id. The URL form is kebab-case. The
 * helper below normalizes both forms to a single lookup key.
 */
import type { DomainType } from '@/api/types';
import type { EngineDomain } from '@/data/segments';
import { SEGMENTS } from '@/data/segments';
import enSegments from '@/i18n/locales/en/segments.json';

export interface SparkStarterPrefill {
  /** Kebab-case slug as it appears in `?starter=<slug>` URLs. */
  slug: string;
  /** English-language starter title (from segments.json). */
  name: string;
  /** English oneliner — used as the Spark Step 1 description seed. */
  description: string;
  /** Engine domain inherited from the parent segment. */
  domain: DomainType;
}

/** Convert a kebab-case slug (URL form) to snake_case (data form). */
function snakeCase(slug: string): string {
  return slug.replace(/-/g, '_');
}

/** Convert a snake_case id (data form) to kebab-case (URL form). */
function kebabCase(id: string): string {
  return id.replace(/_/g, '-');
}

/** Map a segment's `engineDomain` (closed enum from segments.ts) to
 *  the wire-form `DomainType` SparkPage feeds into Step 1. The
 *  domains carried in `engineDomain` are already valid Spark
 *  variants — no conversion needed for these eight. */
function toDomainType(d: EngineDomain): DomainType {
  return d;
}

interface StartersJsonShape {
  starters?: Record<string, Record<string, { name: string; oneliner: string }>>;
}

const startersJson = (enSegments as StartersJsonShape).starters ?? {};

/** Flat lookup keyed by **kebab-case** slug. Built once at module
 *  load — 13 ready segments × ~5 starters = ~65 entries. Coming-soon
 *  segments are still indexed (their starters appear in segments.ts);
 *  the SparkPage doesn't gate on segment status when prefilling. */
const STARTER_PREFILLS: Record<string, SparkStarterPrefill> = (() => {
  const out: Record<string, SparkStarterPrefill> = {};
  for (const segment of SEGMENTS) {
    const localizedStarters = startersJson[segment.id] ?? {};
    for (const starter of segment.starters) {
      const localized = localizedStarters[starter.id];
      if (!localized) continue;
      const slug = kebabCase(starter.id);
      out[slug] = {
        slug,
        name: localized.name,
        description: localized.oneliner,
        domain: toDomainType(segment.engineDomain),
      };
    }
  }
  return out;
})();

/** Lookup a starter prefill by kebab-case slug. Accepts either
 *  `achievement-proof` (URL form) or `achievement_proof` (data
 *  form) — both normalize to the same record. */
export function lookupStarterPrefill(slug: string): SparkStarterPrefill | undefined {
  const trimmed = slug.trim();
  if (!trimmed) return undefined;
  // Try kebab-case form first (the URL form), then snake_case.
  return STARTER_PREFILLS[trimmed] ?? STARTER_PREFILLS[kebabCase(snakeCase(trimmed))];
}

/** Test-only export: the full prefill registry. Useful for asserting
 *  inventory in unit tests without re-implementing the join. */
export const __STARTER_PREFILLS__ = STARTER_PREFILLS;
